import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const iso = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function Month({ year, month, blocked, checkIn, checkOut, onSelect }) {
  const startDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const now = new Date();
  const today = iso(now.getFullYear(), now.getMonth(), now.getDate());

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <p className="font-display text-lg text-center text-[#16301f]">
        {MONTHS[month]} <span className="text-[#16301f]/45">{year}</span>
      </p>
      <div className="grid grid-cols-7 mt-3 text-center">
        {DAYS.map((d, i) => (
          <p key={i} className="text-[10px] uppercase tracking-widest text-[#16301f]/40 py-1">
            {d}
          </p>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={`e${i}`} />;
          const dateStr = iso(year, month, d);
          const isPast = dateStr < today;
          const isBlocked = blocked.has(dateStr);
          const isStart = dateStr === checkIn;
          const isEnd = dateStr === checkOut;
          const inRange = checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;
          const disabled = isPast || isBlocked;
          let cls = "py-1.5 text-xs transition-colors duration-200 ";
          if (isStart || isEnd) cls += "bg-[#16301f] text-[#f7f2e7] ";
          else if (inRange) cls += "bg-[#16301f]/10 text-[#16301f] ";
          else if (isBlocked) cls += "text-[#16301f]/30 line-through decoration-[#16301f]/40 ";
          else if (isPast) cls += "text-[#16301f]/25 ";
          else cls += "text-[#16301f]/80 hover:bg-[#16301f]/15 cursor-pointer ";
          return (
            <button
              key={dateStr}
              type="button"
              data-testid={`cal-day-${dateStr}`}
              disabled={disabled}
              onClick={() => onSelect(dateStr)}
              className={cls}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AvailabilityCalendar({ blocked, checkIn, checkOut, onSelect }) {
  const now = new Date();
  const [offset, setOffset] = useState(0);
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const second = new Date(base.getFullYear(), base.getMonth() + 1, 1);

  return (
    <div data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#16301f]/60">Check availability</p>
          <p className="font-script text-2xl text-[#1a3324] mt-1">Pick your dates</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="cal-prev-month"
            aria-label="Previous month"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - 1))}
            className="border border-[#16301f]/25 p-2 text-[#16301f] hover:bg-[#16301f]/10 disabled:opacity-30 transition-colors duration-300"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            data-testid="cal-next-month"
            aria-label="Next month"
            disabled={offset >= 11}
            onClick={() => setOffset((o) => Math.min(11, o + 1))}
            className="border border-[#16301f]/25 p-2 text-[#16301f] hover:bg-[#16301f]/10 disabled:opacity-30 transition-colors duration-300"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-8">
        <Month year={base.getFullYear()} month={base.getMonth()} blocked={blocked} checkIn={checkIn} checkOut={checkOut} onSelect={onSelect} />
        <Month year={second.getFullYear()} month={second.getMonth()} blocked={blocked} checkIn={checkIn} checkOut={checkOut} onSelect={onSelect} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#16301f]/55">
          <span className="w-3 h-3 bg-[#16301f] inline-block" /> Your dates
        </span>
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#16301f]/55">
          <span className="relative w-3 h-3 inline-block">
            <span className="absolute inset-x-0 top-1/2 h-px bg-[#16301f]/50" />
          </span>{" "}
          Booked
        </span>
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#16301f]/55">
          <span className="w-3 h-3 border border-[#16301f]/60 inline-block" /> Available
        </span>
      </div>
    </div>
  );
}
